import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Alert, Button, Snackbar } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { preferences } from '@/services/preferences';

export const ASANA_BANNER_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
const PREFERENCES_KEY = ['preferences', 'me'] as const;

/**
 * "Connect your Asana account" nudge, shown to signed-in users who have not.
 * Dismissal is stored on the account (preferences) so it follows the user
 * across browsers, and lapses after two weeks. Also confirms a completed
 * connection on whatever page the OAuth round trip returned to.
 */
export function AsanaConnectBanner() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { pathname, search } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isConfirmed, setIsConfirmed] = useState(false);

  const connection = useQuery({
    queryKey: ['asana-connection'],
    queryFn: () => api.asana.getConnection(),
    staleTime: 60_000,
  });
  const prefs = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: () => preferences.getMine(),
    staleTime: 60_000,
  });

  const dismiss = useMutation({
    mutationFn: () => preferences.dismissAsanaBanner(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  });

  // The Profile page shows its own result for this flag.
  const isProfile = pathname === '/profile';
  useEffect(() => {
    if (isProfile || searchParams.get('asana') !== 'connected') return;
    setIsConfirmed(true);
    void queryClient.invalidateQueries({ queryKey: ['asana-connection'] });
    searchParams.delete('asana');
    setSearchParams(searchParams, { replace: true });
  }, [isProfile, searchParams, setSearchParams, queryClient]);

  async function handleConnect() {
    try {
      const { authorizeUrl } = await api.asana.getConnectUrl(undefined, `${pathname}${search}`);
      window.location.href = authorizeUrl;
    } catch {
      // Same failure the Profile page's button has: the user can retry from there.
    }
  }

  const dismissedAt = prefs.data?.asanaBannerDismissedAt;
  const isSnoozed =
    !!dismissedAt && Date.now() - new Date(dismissedAt).getTime() < ASANA_BANNER_SNOOZE_MS;
  const isShown =
    connection.data?.connected === false &&
    connection.data.oauthConfigured !== false &&
    !!prefs.data &&
    !isSnoozed &&
    !isProfile;

  return (
    <>
      {isShown && (
        <Alert
          severity="info"
          closeText={t('auth.profile.asanaBannerDismiss')}
          onClose={() => dismiss.mutate()}
          sx={{ mb: 2, alignItems: 'center' }}
        >
          {t('auth.profile.asanaBannerMessage')}
          <Button color="inherit" size="small" onClick={handleConnect} sx={{ ml: 1, fontWeight: 'bold' }}>
            {t('auth.profile.asanaBannerConnect')}
          </Button>
        </Alert>
      )}
      <Snackbar
        open={isConfirmed}
        autoHideDuration={4000}
        onClose={() => setIsConfirmed(false)}
        message={t('auth.profile.asanaConnected')}
      />
    </>
  );
}
